import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Trash2, Square, Circle, RectangleHorizontal,
  DoorOpen, ScanLine, Wine, Sprout, Pencil, X, RotateCw,
} from "lucide-react";

/* ===========================================================
   SONARA — Raumplan (Floor Plan)
   Interaktive Restaurant-Karte: Räume, Tische frei verschiebbar,
   automatisch gerenderte Stühle, Statusfarben, platzierbare Elemente.
=========================================================== */

const CANVAS_W = 1080;
const CANVAS_H = 720;
const GRID = 20;
const snap = (n) => Math.round(n / GRID) * GRID;

const ROOM_TYPES = [
  { key: "dining",  label: "Hauptsaal" },
  { key: "terrace", label: "Terrasse"  },
  { key: "lounge",  label: "Lounge"    },
  { key: "bar",     label: "Bar"       },
  { key: "vip",     label: "VIP-Raum"  },
];

const ELEMENT_KINDS = [
  { kind: "wall",   label: "Trennwand", icon: ScanLine,           defaultSize: { width: 220, height: 16 } },
  { kind: "door",   label: "Tür",       icon: DoorOpen,           defaultSize: { width: 80, height: 16 } },
  { kind: "window", label: "Fenster",   icon: RectangleHorizontal, defaultSize: { width: 160, height: 12 } },
  { kind: "bar",    label: "Bar",       icon: Wine,                defaultSize: { width: 260, height: 60 } },
  { kind: "plant",  label: "Pflanze",   icon: Sprout,              defaultSize: { width: 48, height: 48 } },
];

const SHAPES = [
  { key: "square",    label: "Quadrat",     icon: Square },
  { key: "round",     label: "Rund",        icon: Circle },
  { key: "rectangle", label: "Rechteckig",  icon: RectangleHorizontal },
];

/* ============================== MAIN ============================== */
export default function FloorPlan() {
  const [rooms, setRooms] = useState([]);
  const [tables, setTables] = useState([]);
  const [elements, setElements] = useState([]);
  const [activeRoomId, setActiveRoomId] = useState(null);
  const [selectedTableId, setSelectedTableId] = useState(null);
  const [selectedElementId, setSelectedElementId] = useState(null);
  const [showRoomForm, setShowRoomForm] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [rR, tR, eR] = await Promise.all([
        api.get("/rooms"), api.get("/tables"), api.get("/floor-elements"),
      ]);
      setRooms(rR.data);
      setTables(tR.data);
      setElements(eR.data);
      if (!activeRoomId && rR.data.length) setActiveRoomId(rR.data[0].room_id);
    } catch (e) {
      toast.error("Daten konnten nicht geladen werden");
    }
  }, [activeRoomId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const activeRoom = useMemo(() => rooms.find((r) => r.room_id === activeRoomId), [rooms, activeRoomId]);
  const roomTables = useMemo(() => tables.filter((t) => t.room_id === activeRoomId), [tables, activeRoomId]);
  const roomElements = useMemo(() => elements.filter((e) => e.room_id === activeRoomId), [elements, activeRoomId]);
  const selectedTable = useMemo(() => roomTables.find((t) => t.table_id === selectedTableId), [roomTables, selectedTableId]);

  /* ---- Mutations ---- */
  const persistTable = async (id, patch) => {
    setTables((curr) => curr.map((t) => (t.table_id === id ? { ...t, ...patch } : t)));
    try { await api.patch(`/tables/${id}`, patch); } catch { toast.error("Speichern fehlgeschlagen"); loadAll(); }
  };
  const persistElement = async (id, patch) => {
    setElements((curr) => curr.map((e) => (e.element_id === id ? { ...e, ...patch } : e)));
    try { await api.patch(`/floor-elements/${id}`, patch); } catch { toast.error("Speichern fehlgeschlagen"); loadAll(); }
  };
  const addTable = async () => {
    if (!activeRoomId) return;
    const seats = 2;
    const next = (roomTables.length || 0) + 1;
    try {
      const { data } = await api.post("/tables", {
        room_id: activeRoomId, name: `T${String(next).padStart(2, "0")}`,
        seats, position_x: 120, position_y: 120, shape: "round",
      });
      setTables((c) => [...c, data]);
      setSelectedTableId(data.table_id);
    } catch { toast.error("Tisch konnte nicht angelegt werden"); }
  };
  const deleteTable = async (id) => {
    await api.delete(`/tables/${id}`);
    setTables((c) => c.filter((t) => t.table_id !== id));
    setSelectedTableId(null);
  };
  const addElement = async (kind) => {
    if (!activeRoomId) return;
    const def = ELEMENT_KINDS.find((k) => k.kind === kind);
    try {
      const { data } = await api.post("/floor-elements", {
        room_id: activeRoomId, kind, x: 80, y: 80,
        width: def.defaultSize.width, height: def.defaultSize.height, rotation: 0,
      });
      setElements((c) => [...c, data]);
    } catch { toast.error("Element konnte nicht hinzugefügt werden"); }
  };
  const deleteElement = async (id) => {
    await api.delete(`/floor-elements/${id}`);
    setElements((c) => c.filter((e) => e.element_id !== id));
  };

  /* ============================== RENDER ============================== */
  return (
    <div className="px-6 lg:px-12 py-12 max-w-[1400px] mx-auto">
      <header className="mb-10 animate-fade-up">
        <h1 className="font-editorial italic text-5xl sm:text-6xl leading-[1.05] tracking-tight text-[var(--text-primary)]" data-testid="floor-plan-title">Raumplan</h1>
        <p className="text-[var(--text-muted)] text-sm mt-3 max-w-xl font-light">
          Eine ruhige Übersicht Ihres Restaurants. Räume bearbeiten, Tische platzieren, Service vorbereiten.
        </p>
      </header>

      {/* Räume Tabs */}
      <div className="flex flex-wrap items-center gap-1 mb-6">
        {rooms.map((r) => (
          <button
            key={r.room_id}
            data-testid={`room-tab-${r.room_id}`}
            onClick={() => { setActiveRoomId(r.room_id); setSelectedTableId(null); }}
            className={`relative px-4 py-2.5 text-[12px] tracking-[0.18em] uppercase transition-colors duration-[700ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
              r.room_id === activeRoomId
                ? "text-[var(--text-primary)]"
                : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            }`}
          >
            {r.name}
            {r.room_id === activeRoomId && (
              <span aria-hidden className="absolute left-3 right-3 -bottom-0.5 h-px"
                    style={{ background: "linear-gradient(90deg, transparent, var(--gold), transparent)" }} />
            )}
          </button>
        ))}
        {activeRoom && (
          <button
            onClick={() => { setEditingRoom(activeRoom); setShowRoomForm(true); }}
            className="ml-2 text-[var(--text-dim)] hover:text-[var(--gold)] transition-colors duration-300"
            data-testid="edit-room-btn"
            title="Raum bearbeiten"
          >
            <Pencil className="w-3.5 h-3.5" strokeWidth={1.4} />
          </button>
        )}
        <button
          onClick={() => { setEditingRoom(null); setShowRoomForm(true); }}
          data-testid="add-room-btn"
          className="ml-4 inline-flex items-center gap-1.5 text-[11px] tracking-[0.22em] uppercase text-[var(--text-muted)] hover:text-[var(--gold)] transition-colors duration-300"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={1.4} /> Raum hinzufügen
        </button>
      </div>

      {!activeRoom ? (
        <div className="surface-1 rounded-md p-16 text-center">
          <div className="font-editorial italic text-2xl text-[var(--text-muted)] mb-4">Noch kein Raum angelegt.</div>
          <button onClick={() => { setEditingRoom(null); setShowRoomForm(true); }} className="btn-primary text-[11px] tracking-[0.2em] uppercase">
            <Plus className="w-3.5 h-3.5 inline mr-1.5" /> Ersten Raum anlegen
          </button>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_280px] gap-6">
          {/* ===== Canvas ===== */}
          <FloorCanvas
            tables={roomTables}
            elements={roomElements}
            selectedTableId={selectedTableId}
            selectedElementId={selectedElementId}
            onSelectTable={(id) => { setSelectedTableId(id); setSelectedElementId(null); }}
            onSelectElement={(id) => { setSelectedElementId(id); setSelectedTableId(null); }}
            onClearSelection={() => { setSelectedTableId(null); setSelectedElementId(null); }}
            onMoveTable={(id, x, y) => persistTable(id, { position_x: x, position_y: y })}
            onMoveElement={(id, x, y) => persistElement(id, { x, y })}
            onRotateElement={(id, rot) => persistElement(id, { rotation: rot })}
            onDeleteElement={deleteElement}
          />

          {/* ===== Tool palette / Inspector ===== */}
          <aside className="space-y-6">
            <ToolPalette onAddTable={addTable} onAddElement={addElement} />
            {selectedTable && (
              <TableInspector
                table={selectedTable}
                onPatch={(p) => persistTable(selectedTable.table_id, p)}
                onDelete={() => deleteTable(selectedTable.table_id)}
                onDeselect={() => setSelectedTableId(null)}
              />
            )}
            {!selectedTable && roomElements.length > 0 && (
              <ElementsList elements={roomElements} onDelete={deleteElement} />
            )}
          </aside>
        </div>
      )}

      {showRoomForm && (
        <RoomFormModal
          room={editingRoom}
          onClose={() => setShowRoomForm(false)}
          onSaved={async () => { setShowRoomForm(false); await loadAll(); }}
          onDeleted={async () => {
            setShowRoomForm(false);
            setActiveRoomId(null);
            await loadAll();
          }}
        />
      )}
    </div>
  );
}

/* ============================== CANVAS ============================== */
function FloorCanvas({
  tables, elements,
  selectedTableId, selectedElementId,
  onSelectTable, onSelectElement, onClearSelection,
  onMoveTable, onMoveElement, onRotateElement, onDeleteElement,
}) {
  const ref = useRef(null);

  return (
    <div
      ref={ref}
      className="relative rounded-md overflow-hidden surface-1"
      style={{
        height: CANVAS_H,
        backgroundColor: "#1A1612",
        backgroundImage: `
          radial-gradient(circle at 20% 20%, rgba(184,150,86,0.05), transparent 60%),
          radial-gradient(circle at 80% 90%, rgba(184,150,86,0.04), transparent 60%),
          linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)
        `,
        backgroundSize: `auto, auto, ${GRID}px ${GRID}px, ${GRID}px ${GRID}px`,
        boxShadow: "inset 0 0 80px rgba(0,0,0,0.4)",
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClearSelection(); }}
      data-testid="floor-canvas"
    >
      {/* decoration elements */}
      {elements.map((el) => (
        <FloorElementNode
          key={el.element_id} el={el} canvasRef={ref}
          selected={el.element_id === selectedElementId}
          onSelect={() => onSelectElement(el.element_id)}
          onMove={(x, y) => onMoveElement(el.element_id, x, y)}
          onRotate={(rot) => onRotateElement(el.element_id, rot)}
          onDelete={() => onDeleteElement(el.element_id)}
        />
      ))}
      {/* tables */}
      {tables.map((t) => (
        <TableNode
          key={t.table_id} table={t} selected={t.table_id === selectedTableId}
          canvasRef={ref}
          onSelect={() => onSelectTable(t.table_id)}
          onMove={(x, y) => onMoveTable(t.table_id, x, y)}
        />
      ))}
    </div>
  );
}

/* ============================== DRAGGABLE PRIMITIVES ==============================
   Both TableNode and FloorElementNode use their own cursor-tracked drag handler
   so each can apply its own coordinate model (center vs. top-left) and clamp.
================================================================================ */

/* ============================== TABLE NODE ============================== */
function TableNode({ table, selected, canvasRef, onSelect, onMove }) {
  const [pos, setPos] = useState({ x: table.position_x || 0, y: table.position_y || 0 });
  useEffect(() => { setPos({ x: table.position_x || 0, y: table.position_y || 0 }); }, [table.position_x, table.position_y]);

  const isFree = table.status === "free";
  const palette = isFree
    ? { ring: "rgba(107, 142, 114, 0.55)",  fill: "rgba(60, 82, 68, 0.85)",  glow: "rgba(107, 142, 114, 0.25)", chairTone: "#3F5546" }
    : { ring: "rgba(166, 107, 98, 0.60)",   fill: "rgba(92, 50, 46, 0.88)",  glow: "rgba(166, 107, 98, 0.30)", chairTone: "#5E3A33" };

  // Footprint depending on shape and seats
  const seats = Math.max(1, Math.min(20, table.seats || 2));
  const isRound = table.shape === "round";
  const isRect = table.shape === "rectangle";
  const baseSize = isRect ? { w: 92 + Math.max(0, seats - 4) * 18, h: 56 } : { w: isRound ? 72 + (seats > 4 ? 20 : 0) : 70, h: isRound ? 72 + (seats > 4 ? 20 : 0) : 70 };
  const halfW = baseSize.w / 2;
  const halfH = baseSize.h / 2;

  // Cursor-tracked drag with grab offset (so the table doesn't jump on click)
  const grabOffset = useRef({ dx: 0, dy: 0 });
  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect();
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cursorX = e.clientX - rect.left;
    const cursorY = e.clientY - rect.top;
    grabOffset.current = { dx: pos.x - cursorX, dy: pos.y - cursorY };

    const apply = (ev, committed) => {
      const r = canvasRef.current?.getBoundingClientRect();
      if (!r) return;
      let nx = (ev.clientX - r.left) + grabOffset.current.dx;
      let ny = (ev.clientY - r.top)  + grabOffset.current.dy;
      // clamp using actual half-size so tables can sit right against the edge
      nx = Math.max(halfW, Math.min(CANVAS_W - halfW, nx));
      ny = Math.max(halfH, Math.min(CANVAS_H - halfH, ny));
      if (committed) { nx = snap(nx); ny = snap(ny); onMove(nx, ny); }
      setPos({ x: nx, y: ny });
    };
    const onMove2 = (ev) => apply(ev, false);
    const onUp = (ev) => {
      apply(ev, true);
      window.removeEventListener("mousemove", onMove2);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove2);
    window.addEventListener("mouseup", onUp);
  };

  return (
    <div
      className="absolute select-none"
      style={{ left: pos.x, top: pos.y, transform: "translate(-50%, -50%)", touchAction: "none" }}
      onMouseDown={handleMouseDown}
      data-testid={`table-node-${table.table_id}`}
    >
      {/* chairs (rendered behind the table) */}
      <Chairs seats={seats} shape={table.shape} baseSize={baseSize} tone={palette.chairTone} />

      {/* table */}
      <div
        className="absolute left-1/2 top-1/2 cursor-grab active:cursor-grabbing transition-shadow duration-300"
        style={{
          width: baseSize.w, height: baseSize.h,
          transform: "translate(-50%, -50%)",
          borderRadius: isRound ? 9999 : 8,
          background: palette.fill,
          border: `1px solid ${palette.ring}`,
          boxShadow: `0 8px 22px -8px ${palette.glow}, 0 0 0 ${selected ? 2 : 0}px var(--gold)`,
        }}
      >
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <div className="font-editorial italic text-[16px] leading-none text-[var(--parchment)]">{table.name}</div>
          <div className="text-[10px] mt-1 tracking-[0.14em] uppercase text-[var(--text-muted)]">{seats} Pl.</div>
        </div>
      </div>
    </div>
  );
}

/* ============================== CHAIRS ============================== */
function Chairs({ seats, shape, baseSize, tone }) {
  const w = baseSize.w, h = baseSize.h;
  const isRound = shape === "round";
  const isRect = shape === "rectangle";
  const chairSize = 14;
  const offset = 16;

  const chairs = [];
  if (isRound) {
    const radius = Math.max(w, h) / 2 + offset + chairSize / 2;
    for (let i = 0; i < seats; i++) {
      const angle = (i / seats) * Math.PI * 2 - Math.PI / 2;
      const cx = Math.cos(angle) * radius;
      const cy = Math.sin(angle) * radius;
      chairs.push({ cx, cy, rot: (angle * 180 / Math.PI) + 90 });
    }
  } else if (isRect) {
    // Rectangle — chairs along top & bottom (mostly), corners go to ends
    const sides = Math.ceil(seats / 2);
    const spacing = w / (sides + 1);
    for (let i = 0; i < sides; i++) {
      chairs.push({ cx: -w / 2 + spacing * (i + 1), cy: -h / 2 - offset, rot: 0 });
    }
    for (let i = 0; i < seats - sides; i++) {
      chairs.push({ cx: -w / 2 + spacing * (i + 1), cy: h / 2 + offset, rot: 180 });
    }
  } else {
    // Square — distribute evenly over 4 sides
    const perSide = [0, 0, 0, 0];
    for (let i = 0; i < seats; i++) perSide[i % 4]++;
    // 0=top,1=right,2=bottom,3=left
    const positions = [];
    const placeSide = (count, side) => {
      for (let i = 0; i < count; i++) {
        const t = (i + 1) / (count + 1);
        if (side === 0) positions.push({ cx: -w / 2 + w * t, cy: -h / 2 - offset, rot: 0 });
        if (side === 1) positions.push({ cx: w / 2 + offset, cy: -h / 2 + h * t, rot: 90 });
        if (side === 2) positions.push({ cx: -w / 2 + w * t, cy: h / 2 + offset, rot: 180 });
        if (side === 3) positions.push({ cx: -w / 2 - offset, cy: -h / 2 + h * t, rot: 270 });
      }
    };
    placeSide(perSide[0], 0);
    placeSide(perSide[1], 1);
    placeSide(perSide[2], 2);
    placeSide(perSide[3], 3);
    chairs.push(...positions);
  }

  return (
    <div className="absolute left-1/2 top-1/2" style={{ width: 0, height: 0 }}>
      {chairs.map((c, i) => (
        <div
          key={i}
          className="absolute"
          style={{
            left: c.cx, top: c.cy,
            width: chairSize, height: chairSize,
            background: tone,
            border: "1px solid rgba(184,150,86,0.18)",
            borderRadius: 3,
            transform: `translate(-50%, -50%) rotate(${c.rot}deg)`,
            boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
          }}
          aria-hidden
        />
      ))}
    </div>
  );
}

/* ============================== FLOOR ELEMENT NODE ============================== */
function FloorElementNode({ el, canvasRef, selected, onSelect, onMove, onRotate, onDelete }) {
  const [pos, setPos] = useState({ x: el.x, y: el.y });
  const [rot, setRot] = useState(el.rotation || 0);
  useEffect(() => { setPos({ x: el.x, y: el.y }); }, [el.x, el.y]);
  useEffect(() => { setRot(el.rotation || 0); }, [el.rotation]);

  const grabOffset = useRef({ dx: 0, dy: 0 });
  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect();
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cursorX = e.clientX - rect.left;
    const cursorY = e.clientY - rect.top;
    grabOffset.current = { dx: pos.x - cursorX, dy: pos.y - cursorY };

    // Rotation-aware bounding box. The element is rotated around its visual
    // center (top-left + w/2, h/2). Its axis-aligned bounding box expands as
    // it rotates, so the clamp must use the rotated bbox — otherwise a 160x12
    // window at 90° still gets clamped with width=160 and can't reach the edge.
    const rad = (rot * Math.PI) / 180;
    const c = Math.abs(Math.cos(rad));
    const s = Math.abs(Math.sin(rad));
    const bboxW = el.width * c + el.height * s;
    const bboxH = el.width * s + el.height * c;
    // Center constraint: center in [bbox/2, CANVAS - bbox/2]
    // → pos (top-left) in [bbox/2 - w/2, CANVAS - bbox/2 - w/2]
    const minX = bboxW / 2 - el.width / 2;
    const maxX = CANVAS_W - bboxW / 2 - el.width / 2;
    const minY = bboxH / 2 - el.height / 2;
    const maxY = CANVAS_H - bboxH / 2 - el.height / 2;

    const apply = (ev, committed) => {
      const r = canvasRef.current?.getBoundingClientRect();
      if (!r) return;
      let nx = (ev.clientX - r.left) + grabOffset.current.dx;
      let ny = (ev.clientY - r.top)  + grabOffset.current.dy;
      nx = Math.max(minX, Math.min(maxX, nx));
      ny = Math.max(minY, Math.min(maxY, ny));
      if (committed) { nx = snap(nx); ny = snap(ny); onMove(nx, ny); }
      setPos({ x: nx, y: ny });
    };
    const onMove2 = (ev) => apply(ev, false);
    const onUp = (ev) => {
      apply(ev, true);
      window.removeEventListener("mousemove", onMove2);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove2);
    window.addEventListener("mouseup", onUp);
  };

  // Rotation drag: angle from element center to mouse
  const startRotate = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cx = rect.left + pos.x + el.width / 2;
    const cy = rect.top  + pos.y + el.height / 2;
    const initialAngle = Math.atan2(e.clientY - cy, e.clientX - cx) * 180 / Math.PI;
    const initialRot = rot;
    const handleMove = (ev) => {
      const a = Math.atan2(ev.clientY - cy, ev.clientX - cx) * 180 / Math.PI;
      let next = initialRot + (a - initialAngle);
      // snap to 5° when SHIFT
      if (ev.shiftKey) next = Math.round(next / 15) * 15;
      next = ((next % 360) + 360) % 360;
      setRot(next);
    };
    const handleUp = (ev) => {
      let final = rot;
      const a = Math.atan2(ev.clientY - cy, ev.clientX - cx) * 180 / Math.PI;
      final = initialRot + (a - initialAngle);
      if (ev.shiftKey) final = Math.round(final / 15) * 15;
      final = ((final % 360) + 360) % 360;
      onRotate(Math.round(final));
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
  };

  const styles = {
    wall:   { background: "rgba(60, 50, 40, 0.85)", border: "1px solid rgba(184,150,86,0.22)" },
    door:   { background: "rgba(60, 50, 40, 0.6)",  border: "1px dashed rgba(184,150,86,0.5)" },
    window: { background: "rgba(96, 130, 168, 0.18)", border: "1px solid rgba(96,130,168,0.45)", boxShadow: "inset 0 0 12px rgba(96,130,168,0.18)" },
    bar:    { background: "linear-gradient(180deg, rgba(95,68,42,0.85), rgba(70,48,28,0.85))", border: "1px solid rgba(184,150,86,0.32)", borderRadius: 4 },
    plant:  { background: "radial-gradient(circle at 50% 35%, rgba(107,142,114,0.85), rgba(60,82,68,0.85))", border: "1px solid rgba(107,142,114,0.5)", borderRadius: "50%" },
  };
  const labels = { wall: "Trennwand", door: "Tür", window: "Fenster", bar: "Bar", plant: "Pflanze" };

  // Outer wrapper carries position. Inner wrapper carries rotation so handles
  // (delete / rotate) rotate with the element and stay attached.
  return (
    <div
      className="absolute"
      style={{ left: pos.x, top: pos.y, width: el.width, height: el.height }}
      data-testid={`element-node-${el.element_id}`}
    >
      <div
        className="relative w-full h-full cursor-grab active:cursor-grabbing"
        style={{
          transform: `rotate(${rot}deg)`,
          transformOrigin: "center center",
          transition: "box-shadow 300ms cubic-bezier(0.22,1,0.36,1)",
          boxShadow: selected ? "0 0 0 1.5px var(--gold), 0 0 24px rgba(184,150,86,0.18)" : "none",
        }}
        onMouseDown={handleMouseDown}
        title={labels[el.kind]}
      >
        <div className="absolute inset-0" style={styles[el.kind]} />
        {el.kind === "bar" && (
          <div className="absolute inset-0 flex items-center justify-center text-[10px] uppercase tracking-[0.28em] text-[var(--parchment)]/70 pointer-events-none">
            Bar
          </div>
        )}

        {selected && (
          <>
            {/* Rotation handle — sits above element, brass dot */}
            <button
              type="button"
              onMouseDown={startRotate}
              data-testid={`element-rotate-${el.element_id}`}
              className="absolute left-1/2 -top-9 -translate-x-1/2 w-7 h-7 rounded-full bg-[#16120E] border border-[var(--gold)]/60 inline-flex items-center justify-center cursor-grab active:cursor-grabbing"
              style={{ boxShadow: "0 0 0 1px rgba(0,0,0,0.4), 0 4px 12px rgba(184,150,86,0.20)" }}
              title="Drehen (Shift = 15° snap)"
            >
              <RotateCw className="w-3.5 h-3.5 text-[var(--gold)]" strokeWidth={1.6} />
            </button>
            {/* connector line */}
            <span aria-hidden className="absolute left-1/2 -top-5 w-px h-5 -translate-x-1/2"
                  style={{ background: "linear-gradient(180deg, var(--gold), transparent)" }} />
            {/* angle label */}
            <span
              className="absolute -bottom-7 left-1/2 -translate-x-1/2 text-[9px] tracking-[0.22em] uppercase text-[var(--gold)] whitespace-nowrap pointer-events-none"
              style={{ transform: `translateX(-50%) rotate(${-rot}deg)` }}
              data-testid={`element-angle-${el.element_id}`}
            >
              {Math.round(rot)}°
            </span>
            {/* Delete handle */}
            <button
              type="button"
              onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); onDelete(); }}
              data-testid={`element-delete-${el.element_id}`}
              className="absolute -top-3 -right-3 w-6 h-6 rounded-full bg-[#16120E] border border-[#A66B62]/55 inline-flex items-center justify-center text-[#C97A7A] hover:bg-[#A66B62]/15 transition-colors"
              title="Element entfernen"
              style={{ boxShadow: "0 2px 6px rgba(0,0,0,0.4)" }}
            >
              <X className="w-3 h-3" strokeWidth={1.6} />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ============================== TOOL PALETTE ============================== */
function ToolPalette({ onAddTable, onAddElement }) {
  return (
    <div className="surface-1 rounded-md p-5">
      <h3 className="font-editorial italic text-[1.35rem] text-[var(--text-primary)] mb-4">Werkzeuge</h3>
      <button
        onClick={onAddTable}
        data-testid="add-table-btn"
        className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-sm border border-[var(--gold)]/30 text-[11px] tracking-[0.22em] uppercase text-[var(--gold)] hover:bg-[var(--gold)]/5 hover:border-[var(--gold)]/55 transition-colors duration-300"
      >
        <Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> Tisch hinzufügen
      </button>

      <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--text-muted)] mt-6 mb-3">Elemente</div>
      <div className="grid grid-cols-2 gap-2">
        {ELEMENT_KINDS.map((e) => (
          <button
            key={e.kind}
            onClick={() => onAddElement(e.kind)}
            data-testid={`add-element-${e.kind}`}
            className="px-2 py-2.5 rounded-sm border border-white/[0.06] hover:border-[var(--gold)]/30 text-[10px] tracking-[0.18em] uppercase text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors duration-300 inline-flex flex-col items-center gap-1.5"
          >
            <e.icon className="w-4 h-4" strokeWidth={1.3} />
            {e.label}
          </button>
        ))}
      </div>

      <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--text-muted)] mt-6 mb-3">Status</div>
      <div className="space-y-2 text-[11px]">
        <div className="flex items-center gap-3 text-[var(--text-secondary)]">
          <span className="w-3.5 h-3.5 rounded-full" style={{ background: "rgba(60, 82, 68, 0.85)", border: "1px solid rgba(107,142,114,0.55)" }} />
          Frei
        </div>
        <div className="flex items-center gap-3 text-[var(--text-secondary)]">
          <span className="w-3.5 h-3.5 rounded-full" style={{ background: "rgba(92, 50, 46, 0.88)", border: "1px solid rgba(166,107,98,0.60)" }} />
          Besetzt / zugewiesen
        </div>
      </div>
    </div>
  );
}

/* ============================== TABLE INSPECTOR ============================== */
function TableInspector({ table, onPatch, onDelete, onDeselect }) {
  return (
    <div className="surface-1 rounded-md p-5" data-testid="table-inspector">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="overline mb-1">Tisch</div>
          <h3 className="font-editorial italic text-[1.5rem] text-[var(--text-primary)]" data-testid="inspector-name">{table.name}</h3>
        </div>
        <button onClick={onDeselect} className="text-[var(--text-dim)] hover:text-[var(--text-primary)]" data-testid="inspector-close">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="space-y-5">
        <div>
          <label className="text-[10px] text-[var(--text-muted)] uppercase tracking-[0.22em] block mb-2">Name</label>
          <input
            value={table.name}
            onChange={(e) => onPatch({ name: e.target.value })}
            className="linen-input"
            data-testid="inspector-name-input"
          />
        </div>
        <div>
          <label className="text-[10px] text-[var(--text-muted)] uppercase tracking-[0.22em] block mb-2">Sitzanzahl</label>
          <input
            type="number" min="1" max="20"
            value={table.seats}
            onChange={(e) => onPatch({ seats: Math.max(1, parseInt(e.target.value, 10) || 1) })}
            className="linen-input"
            data-testid="inspector-seats-input"
          />
        </div>
        <div>
          <label className="text-[10px] text-[var(--text-muted)] uppercase tracking-[0.22em] block mb-2">Form</label>
          <div className="grid grid-cols-3 gap-2">
            {SHAPES.map((s) => {
              const active = table.shape === s.key;
              return (
                <button
                  key={s.key} type="button"
                  onClick={() => onPatch({ shape: s.key })}
                  data-testid={`inspector-shape-${s.key}`}
                  className={`px-2 py-2 rounded-sm border text-[10px] tracking-[0.18em] uppercase inline-flex flex-col items-center gap-1 transition-colors duration-300 ${
                    active ? "border-[var(--gold)]/55 text-[var(--gold)]" : "border-white/[0.06] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                  }`}
                >
                  <s.icon className="w-3.5 h-3.5" strokeWidth={1.4} />
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <label className="text-[10px] text-[var(--text-muted)] uppercase tracking-[0.22em] block mb-2">Status</label>
          <div className="grid grid-cols-2 gap-2">
            {[{ k: "free", lbl: "Frei" }, { k: "occupied", lbl: "Besetzt" }].map((o) => {
              const active = table.status === o.k;
              return (
                <button
                  key={o.k} type="button"
                  onClick={() => onPatch({ status: o.k })}
                  data-testid={`inspector-status-${o.k}`}
                  className={`px-2 py-2 rounded-sm border text-[10px] tracking-[0.2em] uppercase transition-colors duration-300 ${
                    active ? "border-[var(--gold)]/55 text-[var(--gold)]" : "border-white/[0.06] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                  }`}
                >
                  {o.lbl}
                </button>
              );
            })}
          </div>
        </div>
        <button
          onClick={() => { if (window.confirm("Tisch löschen?")) onDelete(); }}
          data-testid="inspector-delete"
          className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 mt-2 rounded-sm border border-[#A66B62]/30 text-[10px] tracking-[0.22em] uppercase text-[#C97A7A] hover:bg-[#A66B62]/10 transition-colors duration-300"
        >
          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.4} /> Tisch entfernen
        </button>
      </div>
    </div>
  );
}

/* ============================== ELEMENTS LIST ============================== */
function ElementsList({ elements, onDelete }) {
  const meta = {
    wall:   { label: "Trennwand", icon: ScanLine },
    door:   { label: "Tür",       icon: DoorOpen },
    window: { label: "Fenster",   icon: RectangleHorizontal },
    bar:    { label: "Bar",       icon: Wine },
    plant:  { label: "Pflanze",   icon: Sprout },
  };
  return (
    <div className="surface-1 rounded-md p-5">
      <div className="overline mb-3">Platzierte Elemente</div>
      <div className="space-y-1">
        {elements.map((e) => {
          const m = meta[e.kind];
          const Icon = m.icon;
          return (
            <div
              key={e.element_id}
              className="flex items-center justify-between py-1.5"
              data-testid={`element-row-${e.element_id}`}
            >
              <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.18em] uppercase text-[var(--text-secondary)]">
                <Icon className="w-3.5 h-3.5 text-[var(--text-dim)]" strokeWidth={1.3} />
                {m.label}
              </span>
              <button
                onClick={() => onDelete(e.element_id)}
                className="text-[var(--text-dim)] hover:text-[#C97A7A] transition-colors"
                data-testid={`element-row-delete-${e.element_id}`}
                title="Entfernen"
              >
                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.4} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============================== ROOM FORM MODAL ============================== */
function RoomFormModal({ room, onClose, onSaved, onDeleted }) {
  const [form, setForm] = useState({
    name: room?.name || "",
    type: room?.type || "dining",
    capacity: room?.capacity || 0,
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name erforderlich");
    setBusy(true);
    try {
      if (room) await api.patch(`/rooms/${room.room_id}`, form);
      else await api.post("/rooms", form);
      toast.success(room ? "Raum gespeichert" : "Raum angelegt");
      onSaved();
    } catch { toast.error("Speichern fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Raum „${room.name}" und alle Tische darin löschen?`)) return;
    try { await api.delete(`/rooms/${room.room_id}`); toast.success("Raum gelöscht"); onDeleted(); }
    catch { toast.error("Löschen fehlgeschlagen"); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-md p-6 animate-fade-up"
      style={{ animationDuration: "260ms" }}
      onClick={onClose}
      data-testid="room-form-modal"
    >
      <div
        className="relative w-full max-w-md p-8 bg-[#16120E] rounded-md"
        style={{ boxShadow: "0 1px 0 rgba(184,150,86,0.08) inset, 0 60px 120px -40px rgba(0,0,0,0.7)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <span aria-hidden className="absolute top-0 left-8 right-8 h-px brass-line opacity-80" />
        <div className="flex items-start justify-between mb-6">
          <h3 className="font-editorial italic text-[1.9rem] leading-none text-[var(--text-primary)]">
            {room ? "Raum bearbeiten" : "Neuer Raum"}
          </h3>
          <button onClick={onClose} className="text-[var(--text-dim)] hover:text-[var(--text-primary)]" data-testid="room-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="text-[10px] text-[var(--text-muted)] uppercase tracking-[0.22em] block mb-2">Name *</label>
            <input
              required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Hauptsaal, Terrasse, Lounge…"
              className="linen-input"
              data-testid="room-name-input"
            />
          </div>
          <div>
            <label className="text-[10px] text-[var(--text-muted)] uppercase tracking-[0.22em] block mb-2">Typ</label>
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              className="linen-input cursor-pointer"
              data-testid="room-type-select"
            >
              {ROOM_TYPES.map((rt) => (
                <option key={rt.key} value={rt.key}>{rt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-[var(--text-muted)] uppercase tracking-[0.22em] block mb-2">Kapazität (Richtwert)</label>
            <input
              type="number" min="0" max="500"
              value={form.capacity}
              onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value, 10) || 0 })}
              className="linen-input"
              data-testid="room-capacity-input"
            />
          </div>
          <div className="flex gap-3 pt-3">
            <button type="button" onClick={onClose} className="btn-ghost text-[11px] tracking-[0.2em] uppercase flex-1" data-testid="room-cancel">Abbrechen</button>
            <button type="submit" disabled={busy} className="btn-primary text-[11px] tracking-[0.2em] uppercase flex-1" data-testid="room-submit">
              {busy ? "Speichere…" : (room ? "Speichern" : "Anlegen")}
            </button>
          </div>
          {room && (
            <button
              type="button" onClick={remove}
              className="w-full mt-3 text-[10px] tracking-[0.24em] uppercase text-[#C97A7A] hover:text-[#E89090] transition-colors duration-300"
              data-testid="room-delete"
            >
              Raum löschen
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
